/**
 * Mock API utility that simulates async behavior with configurable delays
 * Use this to fetch mock data as if it came from a real API
 */

import type { UserData } from '../features/settings/user-types'
import type { ChatData } from '../shared/types/chat'
import type { SettingsData } from '../features/settings/settings-types'
import type { NotebookData, NotebookNote } from '../shared/types/notebook'
import type { BriefingConfig } from '../shared/types/briefing'
import type { ApiResponse, ApproveResponse, DenyResponse, SendMessageResponse, UpdateSettingResponse, BriefingSettingResponse, SaveToNotebookResponse } from './types'
import type { CortexEntry } from '../features/cortex/types'
import { formatDisplayTime } from '@tendbot/ui';

// ─── Local types ─────────────────────────────────────────────

type DelaySpeed = 'fast' | 'normal' | 'slow';

interface DelayRange {
  min: number;
  max: number;
}

interface UpdateProfileResponse extends ApiResponse {
  updates: Record<string, unknown>;
}

// ─── Helpers ─────────────────────────────────────────────────

const DELAYS: Record<DelaySpeed, DelayRange> = {
  fast: { min: 200, max: 400 },
  normal: { min: 400, max: 800 },
  slow: { min: 800, max: 1500 },
};

/**
 * Simulates network delay
 */
const simulateDelay = (speed: DelaySpeed = 'normal'): Promise<void> => {
  const { min, max } = DELAYS[speed];
  const delay = Math.floor(Math.random() * (max - min + 1)) + min;
  return new Promise<void>((resolve) => setTimeout(resolve, delay));
};

/**
 * Deep clone an object to prevent mutation of mock data
 */
const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj)) as T;

// ─── Mock API ────────────────────────────────────────────────

/**
 * Mock API object with methods for fetching different data types
 */
export const mockApi = {
  /**
   * Generic fetch with delay
   */
  async fetch<T>(data: T, speed: DelaySpeed = 'normal'): Promise<T> {
    await simulateDelay(speed);
    return deepClone(data);
  },

  /**
   * Fetch with configurable error rate for testing error states
   */
  async fetchWithError<T>(data: T, errorRate: number = 0.1): Promise<T> {
    await simulateDelay('normal');
    if (Math.random() < errorRate) {
      throw new Error('Network error - please try again');
    }
    return deepClone(data);
  },

  /**
   * Get user profile data
   */
  async getUser(): Promise<UserData> {
    const { default: data } = await import('./data/user.json');
    return this.fetch(data as UserData, 'fast');
  },

  /**
   * Get chat threads and configuration
   */
  async getChat(): Promise<ChatData> {
    const { default: data } = await import('./data/chat.json');
    return this.fetch(data as ChatData);
  },

  /**
   * Get settings configuration
   */
  async getSettings(): Promise<SettingsData> {
    const { default: data } = await import('./data/settings.json');
    return this.fetch(data as SettingsData);
  },

  /**
   * Get notebook data
   */
  async getNotebook(): Promise<NotebookData> {
    const { default: data } = await import('./data/notebook.json');
    return this.fetch(data as NotebookData);
  },

  /**
   * Get user preferences (backend-shaped)
   */
  async getPreferences(): Promise<Record<string, unknown>> {
    const { default: data } = await import('./data/user.json');
    const prefs = (data as UserData).preferences;
    return this.fetch(prefs as unknown as Record<string, unknown>, 'fast');
  },

  /**
   * Update user preferences (deep-merge)
   */
  async updatePreferences(updates: Record<string, unknown>): Promise<ApiResponse & { preferences: Record<string, unknown> }> {
    await simulateDelay('fast');
    return {
      success: true,
      preferences: updates,
    };
  },

  /**
   * Get morning briefing configuration
   */
  async getBriefing(): Promise<BriefingConfig> {
    const { default: data } = await import('./data/briefing.json');
    return this.fetch(data as BriefingConfig);
  },

  /**
   * Update a briefing setting
   * @param key - Setting key (e.g. 'enabled', 'time', 'weekdaysOnly', 'sources.calendar.enabled')
   * @param value - New value
   */
  async updateBriefingSetting(key: string, value: unknown): Promise<BriefingSettingResponse> {
    await simulateDelay('fast');
    return {
      success: true,
      key,
      value,
      timestamp: new Date().toISOString(),
    };
  },

  /**
   * Save a note to the notebook
   * @param note - Partial note data
   */
  async saveToNotebook(_note: Partial<NotebookNote>): Promise<SaveToNotebookResponse> {
    await simulateDelay('fast');
    return {
      success: true,
      noteId: `note-${Date.now()}`,
    };
  },

  /**
   * Update an existing note
   * @param noteId - Note ID
   * @param _updates - Fields to update
   */
  async updateNote(noteId: string, _updates: Partial<NotebookNote>): Promise<ApiResponse & { noteId: string }> {
    await simulateDelay('fast');
    return {
      success: true,
      noteId,
    };
  },

  /**
   * Delete a note
   * @param noteId - Note ID
   */
  async deleteNote(noteId: string): Promise<ApiResponse & { noteId: string }> {
    await simulateDelay('fast');
    return {
      success: true,
      noteId,
    };
  },

  /**
   * Toggle pin state of a note
   * @param noteId - Note ID
   */
  async toggleNotePin(noteId: string): Promise<ApiResponse & { noteId: string; pinned: boolean }> {
    await simulateDelay('fast');
    return {
      success: true,
      noteId,
      pinned: true,
    };
  },

  /**
   * Get Cortex entries
   */
  async getCortex(): Promise<CortexEntry[]> {
    const { default: data } = await import('./data/cortex.json');
    return this.fetch(data as CortexEntry[], 'fast');
  },

  /**
   * Check if an assistant email local part is available
   * @param localPart - The part before @tend.bot
   */
  async checkEmailAvailability(localPart: string): Promise<{ available: boolean; suggestions?: string[] }> {
    await simulateDelay('fast');
    const taken = new Set(['alfred', 'victoria', 'gunnar', 'marta', 'frasse', 'bibbi', 'sebastian', 'eleanor', 'tend', 'admin', 'support']);
    if (taken.has(localPart.toLowerCase())) {
      const base = localPart.toLowerCase();
      return {
        available: false,
        suggestions: [`${base}.t`, `${base}.s`, `${base}o`],
      };
    }
    return { available: true };
  },

  /**
   * Delete a Cortex entry
   * @param entryId - Entry ID
   */
  async deleteCortexEntry(entryId: string): Promise<ApiResponse & { entryId: string }> {
    await simulateDelay('fast');
    return {
      success: true,
      entryId,
    };
  },

  // ==================
  // Mutation simulations
  // ==================

  /**
   * Simulate approving an item
   * @param id - Item ID
   */
  async approveItem(id: string): Promise<ApproveResponse> {
    await simulateDelay('fast');
    return {
      success: true,
      id,
      action: 'approved',
      timestamp: new Date().toISOString(),
    };
  },

  /**
   * Simulate denying an item
   * @param id - Item ID
   * @param reason - Optional denial reason
   */
  async denyItem(id: string, reason: string | null = null): Promise<DenyResponse> {
    await simulateDelay('fast');
    return {
      success: true,
      id,
      action: 'denied',
      reason,
      timestamp: new Date().toISOString(),
    };
  },

  /**
   * Simulate sending a chat message and getting a response
   * @param content - User's message content
   */
  async sendMessage(content: string): Promise<SendMessageResponse> {
    await simulateDelay('slow');

    // Determine response based on content keywords
    const lowerContent = content.toLowerCase();
    let response = "I understand your request. Let me look into that for you.";

    if (lowerContent.includes('calendar') || lowerContent.includes('schedule') || lowerContent.includes('meeting')) {
      response = "I've checked your calendar. You have 4 meetings today. Your next one is the Team standup at 10:00 AM. Would you like me to add something to your schedule?";
    } else if (lowerContent.includes('email') || lowerContent.includes('mail')) {
      response = "I can help you with that email. Would you like me to draft something for you, or would you prefer I summarize your inbox?";
    } else if (lowerContent.includes('book') || lowerContent.includes('reserve') || lowerContent.includes('restaurant')) {
      response = "I'd be happy to make a booking for you. Could you tell me the date, time, and number of guests? I'll find the best options available.";
    } else if (lowerContent.includes('approve') || lowerContent.includes('pending')) {
      response = "You have 3 items pending approval. One is high priority - a flight booking to London. Would you like me to summarize them for you?";
    } else if (lowerContent.includes('thank')) {
      response = "You're most welcome, Daniel. Is there anything else I can assist you with?";
    }

    return {
      id: `msg-${Date.now()}`,
      type: 'assistant',
      content: response,
      timestamp: new Date().toISOString(),
      displayTime: formatDisplayTime(),
    };
  },

  /**
   * Get activity summary (value metrics)
   */
  async getActivitySummary(_window: 'week' | 'trial'): Promise<{
    emailsDrafted: number
    approvalsProcessed: number
    followupsTracked: number
    hoursSaved: number
  }> {
    await simulateDelay('fast');
    const emailsDrafted = 12;
    const approvalsProcessed = 8;
    const followupsTracked = 5;
    const totalMinutes = emailsDrafted * 8 + approvalsProcessed * 3 + followupsTracked * 5;
    return {
      emailsDrafted,
      approvalsProcessed,
      followupsTracked,
      hoursSaved: Math.round(totalMinutes / 6) / 10,
    };
  },

  /**
   * Simulate toggling a setting
   * @param settingId - Setting identifier
   * @param value - New value
   */
  async updateSetting(settingId: string, value: boolean): Promise<UpdateSettingResponse> {
    await simulateDelay('fast');
    return {
      success: true,
      settingId,
      value,
    };
  },

  /**
   * Simulate updating user profile
   * @param updates - Fields to update
   */
  async updateProfile(updates: Record<string, unknown>): Promise<UpdateProfileResponse> {
    await simulateDelay('normal');
    return {
      success: true,
      updates,
    };
  },
};

